import {
  Star,
  Lock,
  SummarizeOutlined,
  HistoryOutlined,
  StorefrontOutlined,
  ConstructionOutlined,
  FeedOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import {
  AIPill,
  NavLinkData,
  SerializedVehicleReport,
  useSession,
} from '@ltvco/refresh-lib/v1';
import { ReportMenuItem } from '@ltvco/refresh-lib/v2';

import { useTheme } from '@ltvco/refresh-lib/theme';
import { useReportCountryContext } from '@ltvco/refresh-lib/ctx';

interface VehicleNavLinkData extends NavLinkData {
  visible: boolean;
}

interface IReportMenuItemWithPermissions extends ReportMenuItem {
  isLocked: boolean;
  childItems?: IReportMenuItemWithPermissions[];
}

export const getVehicleNavLinkDataV2 = (
  reportData: SerializedVehicleReport | null,
  complaintsCount: number,
  vehicleSelfInspectionCount: number
) => {
  const theme = useTheme();
  const { isSectionExcluded, reportCountry } = useReportCountryContext();
  const {
    session: { account },
  } = useSession();
  const isCanadian = reportCountry === 'CA';
  const isCAUser = account?.account.subscription_info.user_country === 'CA';
  const showUSPrefix = isCAUser && !isCanadian;

  // AB Tests

  const automobile = reportData?.automobiles?.[0];

  if (!reportData) return [];
  const isYMMSearch = reportData.meta?.search_by === 'ymm';
  const safetyCount =
    reportData.automobiles?.[0]?.safety_ratings.length + complaintsCount;
  const ymmPresent = [
    automobile?.year,
    automobile?.make,
    automobile?.model,
  ].filter(Boolean);
  const hasAllYMMPresent = ymmPresent.length === 3;
  const shouldShowSmartInsightsNavLink =
    !isYMMSearch && hasAllYMMPresent && !isSectionExcluded('smart_insights');

  const navLinkData: IReportMenuItemWithPermissions[] = [
    {
      href: '#vehicle-overview',
      sectionId: '#vehicle-overview',
      labelText: 'Photos',
      reportItemsCount: automobile?.imagesCount || null,
      hideReportItemsCount: false,
      notificationSection: 'identity',
      visible: true,
      isLocked: false,
    },
    {
      labelText: 'Overview',
      name: 'overview',
      icons: [
        <SummarizeOutlined
          key="summary-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      visible: true,
      isLocked: false,
      childItems: [
        {
          href: '#vehicle-overview',
          sectionId: '#vehicle-overview',
          labelText: 'Photos',
          name: 'vehicle-photos',
          reportItemsCount: automobile?.imagesCount || null,
          hideReportItemsCount: false,
          visible: true,
          notificationSection: 'identity',
          isLocked: false,
        },
        {
          href: '#smart-insights',
          sectionId: '#smart-insights',
          labelText: 'Smart Insights',
          name: 'vehicle-smart-insights',
          visible: shouldShowSmartInsightsNavLink,
          reportItemsCount: 0,
          notificationSection: 'smart insights',
          icons: [<AIPill key="api-icon" />],
          showIcon: true,
          isLocked: true,
        },
      ],
    },
    {
      labelText: 'Vehicle Background',
      name: 'vehicle-history',
      href: '#vehicle-history',
      sectionId: '#vehicle-history',
      icons: [
        <HistoryOutlined
          key="history-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      visible: !isYMMSearch,
      isLocked: true,
      childItems: [
        {
          href: '#vehicle-ownership-history',
          sectionId: 'vehicle-ownership-history',
          labelText: 'Ownership History',
          name: 'vehicle-ownership-history',
          reportItemsCount: automobile?.ownership_history?.length || 0,
          hideReportItemsCount: false,
          visible: !isYMMSearch && !isSectionExcluded('ownership_history'),
          isLocked: false,
        },
        {
          href: '#vehicle-title-records',
          sectionId: 'vehicle-title-records',
          labelText: isCanadian ? 'Registrations' : 'Title Records',
          name: 'vehicle-title-records',
          reportItemsCount: automobile?.titleRegistrationsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          icons: [
            <Lock
              key="lock-icon-title-records"
              fontSize="small"
              sx={{
                marginLeft: '10px',
                color: 'text.primary',
              }}
            />,
          ],
          showIcon:
            !isCanadian &&
            !automobile?.nmvtis_success &&
            automobile?.titleRegistrationsCount === 0,
          isLocked: true,
        },
        {
          href: isCanadian ? '#vehicle-status-checks' : '#vehicle-title-brands',
          sectionId: isCanadian
            ? 'vehicle-status-checks'
            : 'vehicle-title-brands',
          labelText: isCanadian ? 'Status Checks' : 'Title Brands',
          name: isCanadian ? 'vehicle-status-checks' : 'vehicle-title-brands',
          reportItemsCount: isCanadian
            ? automobile?.title_registrations?.filter(
                (registration) => registration.condition
              )?.length || 0
            : automobile?.titleChecksCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          icons: [
            <Lock
              key="lock-icon-title-brands"
              fontSize="small"
              sx={{
                marginLeft: '10px',
                color: 'text.primary',
              }}
            />,
          ],
          showIcon:
            !isCanadian &&
            !automobile?.nmvtis_success &&
            automobile?.titleChecksCount === 0,
          isLocked: true,
        },
        {
          href: '#vehicle-liens',
          sectionId: 'vehicle-liens',
          labelText: 'Liens',
          name: 'vehicle-liens',
          reportItemsCount: automobile?.liensCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#vehicle-impounds',
          sectionId: '#vehicle-impounds',
          labelText: `${showUSPrefix ? 'U.S. ' : ''}Impounds & Exports`,
          name: 'vehicle-impounds-exports',
          reportItemsCount:
            (automobile?.impoundsCount || 0) + (automobile?.exportsCount || 0),
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#vehicle-thefts',
          sectionId: '#vehicle-thefts',
          labelText: `${showUSPrefix ? 'U.S. ' : ''}Theft Records`,
          name: 'vehicle-thefts',
          reportItemsCount: automobile?.theftsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: 'vehicle-inspections-section',
          sectionId: 'vehicle-inspections-section',
          name: 'vehicle-inspections',
          labelText: 'Inspection Records',
          reportItemsCount: automobile?.inspectionEventsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch && !isSectionExcluded('inspections'),
          isLocked: true,
        },
        {
          href: '#vehicle-sales',
          sectionId: '#vehicle-sales',
          labelText: 'Sales Listings',
          name: 'vehicle-sales',
          reportItemsCount: automobile?.listingsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#vehicle-auctions',
          sectionId: '#vehicle-auctions',
          labelText: 'Auctions',
          name: 'vehicle-auctions',
          reportItemsCount: automobile?.auctionsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#detailed-history',
          sectionId: '#detailed-history',
          name: 'vehicle-detailed-history',
          labelText: 'Detailed History',
          reportItemsCount: automobile?.detailedHistoryCount,
          hideReportItemsCount: false,
          visible: Boolean(!isYMMSearch && automobile?.detailedHistoryCount),
          notificationSection: 'history',
          isLocked: true,
        },
      ],
    },
    {
      labelText: 'Damages & Repairs',
      name: 'maintenance-repairs',
      href: '#maintenance-repairs',
      sectionId: '#maintenance-repairs',
      icons: [
        <ConstructionOutlined
          key="construction-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      visible: true,
      isLocked: true,
      childItems: [
        {
          href: '#vehicle-accidents',
          sectionId: '#vehicle-accidents',
          labelText: 'Accidents',
          name: 'vehicle-accidents',
          reportItemsCount: automobile?.accidents?.length || 0,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#vehicle-damages',
          sectionId: '#vehicle-damages',
          labelText: 'Damages',
          name: 'vehicle-damages',
          reportItemsCount: automobile?.damageHistoryCount || 0,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#vehicle-repair-quotes',
          sectionId: '#vehicle-repair-quotes',
          labelText: 'Repair Quotes',
          name: 'vehicle-repair-quotes',
          reportItemsCount:
            automobile?.damage_history?.repair_quotes?.length || 0,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#vehicle-salvages',
          sectionId: '#vehicle-salvages',
          labelText: 'Salvages',
          name: 'vehicle-salvages',
          reportItemsCount: automobile?.salvage?.length || 0,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#vehicle-service-and-repairs',
          sectionId: '#vehicle-service-and-repairs',
          name: 'vehicle-service-and-repairs',
          labelText: 'Service History',
          reportItemsCount: automobile?.repairsCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch && !isSectionExcluded('service_and_repairs'),
          notificationSection: 'maintenance',
          isLocked: true,
        },
        {
          href: '#vehicle-maintenance-schedule',
          sectionId: '#vehicle-maintenance-schedule',
          name: 'vehicle-maintenance-schedule',
          labelText: 'Maintenance Schedule',
          reportItemsCount: automobile?.maintenanceCount,
          hideReportItemsCount: false,
          visible: !isYMMSearch,
          isLocked: true,
        },
        {
          href: '#vehicle-recalls',
          sectionId: '#vehicle-recalls',
          name: 'vehicle-recalls',
          labelText: 'Recalls',
          reportItemsCount: isYMMSearch
            ? automobile?.recallsCount
            : automobile?.recallsWithStatusCount,
          hideReportItemsCount: false,
          visible: true,
          notificationSection: 'prices',
          isLocked: true,
        },
        {
          href: '#self-inspection',
          sectionId: '#self-inspection',
          name: 'self-inspection',
          labelText: 'DIY Check',
          reportItemsCount: vehicleSelfInspectionCount,
          hideReportItemsCount: false,
          icons: [
            <Star
              key="star-icon"
              fontSize="small"
              sx={{
                marginLeft: '10px',
                color: 'text.primary',
              }}
            />,
          ],
          showIcon: vehicleSelfInspectionCount === 0,
          visible: !isYMMSearch,
          isLocked: true,
        },
      ],
    },
    {
      labelText: 'Market & Value',
      name: 'market-value',
      href: '#market-value',
      sectionId: '#market-value',
      icons: [
        <StorefrontOutlined
          key="storefront-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      visible: true,
      isLocked: true,
      childItems: [
        {
          href: '#vehicle-market',
          sectionId: '#vehicle-market',
          labelText: 'Market Values',
          name: 'vehicle-market',
          hideReportItemsCount: true,
          visible: true,
          isLocked: true,
        },
        {
          href: '#comparable-vehicles',
          sectionId: '#comparable-vehicles',
          labelText: 'Comps for Sale',
          name: 'vehicle-comps-for-sale',
          hideReportItemsCount: true,
          visible: true,
          isLocked: true,
        },
        {
          href: '#vehicle-ownership-cost',
          sectionId: 'vehicle-ownership-cost',
          labelText: 'Ownership Costs',
          name: 'vehicle-ownership-cost',
          hideReportItemsCount: true,
          visible: true,
          isLocked: true,
        },
      ],
    },
    {
      labelText: 'Features & Specs',
      name: 'features-specs',
      icons: [
        <FeedOutlined
          key="feed-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      visible: true,
      isLocked: false,
      childItems: [
        {
          href: '#vehicle-specs',
          sectionId: '#vehicle-specs',
          name: 'vehicle-specs',
          labelText: 'Vehicle Specs',
          hideReportItemsCount: true,
          notificationSection: 'specifications',
          visible: true,
          isLocked: false,
        },
        {
          href: '#installed-options-packages',
          sectionId: '#installed-options-packages',
          name: 'vehicle-options-packages',
          labelText: 'Options & Packages',
          hideReportItemsCount: true,
          visible: true,
          isLocked: true,
        },
        {
          href: '#equipment-specifications',
          sectionId: '#equipment-specifications',
          name: 'vehicle-equipment',
          labelText: 'Equipment',
          hideReportItemsCount: true,
          visible: true,
          isLocked: true,
        },
      ],
    },
    {
      labelText: 'Safety & Reliability',
      name: 'safety-reliability',
      href: '#safety-reliability',
      sectionId: '#safety-reliability',
      icons: [
        <InfoOutlined
          key="info-icon"
          sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }}
        />,
      ],
      visible: true,
      isLocked: true,
      childItems: [
        {
          href: '#vehicle-safety-ratings',
          sectionId: '#vehicle-safety-ratings',
          name: 'vehicle-safety-ratings',
          labelText: 'Safety Ratings',
          hideReportItemsCount: true,
          visible: true,
          notificationSection: 'safety',
          isLocked: true,
        },
        {
          href: '#vehicle-warranty',
          sectionId: '#vehicle-warranty',
          name: 'vehicle-warranty',
          labelText: 'Warranties',
          hideReportItemsCount: true,
          visible: true,
          notificationSection: 'warranties',
          isLocked: true,
        },
        {
          href: '#vehicle-safety-complaints-section',
          sectionId: '#vehicle-safety-complaints-section',
          name: 'vehicle-safety-complaints-section',
          labelText: 'Complaints',
          hideReportItemsCount: true,
          visible: true,
          notificationSection: 'complaints',
          isLocked: true,
        },
      ],
    },
  ];

  return navLinkData
    .map((item) => ({
      ...item,
      childItems: item.childItems
        ? item.childItems.filter((child) => child.visible)
        : [],
    }))
    .filter((item) => item.visible || item.childItems.length > 0);
};

export const getVehicleNavLinkData = (
  reportData: SerializedVehicleReport | null,
  complaintsCount: number,
  vehicleSelfInspectionCount: number
) => {
  const automobile = reportData?.automobiles?.[0];

  if (!reportData) return [];
  const isYMMSearch = reportData.meta?.search_by === 'ymm';
  const safetyCount =
    reportData.automobiles?.[0]?.safety_ratings.length + complaintsCount;
  const ymmPresent = [
    automobile?.year,
    automobile?.make,
    automobile?.model,
  ].filter(Boolean);
  const hasAllYMMPresent = ymmPresent.length === 3;
  const shouldShowSmartInsightsNavLink = !isYMMSearch && hasAllYMMPresent;

  const navLinkData: VehicleNavLinkData[] = [
    {
      href: '#vehicle-report-overview',
      sectionId: '#vehicle-overview',
      text: 'Photos',
      reportItemsCount: automobile?.imagesCount || null,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'identity',
    },
    (shouldShowSmartInsightsNavLink && {
      href: '#smart-insights',
      sectionId: '#smart-insights',
      text: 'Smart Insights',
      visible: true,
      reportItemsCount: 0,
      notificationSection: 'smart insights',
      icon: <AIPill />,
      showIcon: true,
    }) as VehicleNavLinkData,
    {
      href: '#self-inspection',
      sectionId: '#self-inspection',
      text: 'Self Inspection',
      reportItemsCount: vehicleSelfInspectionCount,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      icon: <Star fontSize="small" sx={{ marginRight: '-6px' }} />,
      showIcon: vehicleSelfInspectionCount === 0,
    },
    {
      href: '#detailed-history',
      sectionId: '#detailed-history',
      text: 'Detailed History',
      reportItemsCount: automobile?.detailedHistoryCount,
      hideReportItemsCount: false,
      visible: Boolean(!isYMMSearch && automobile?.detailedHistoryCount),
      notificationSection: 'history',
    },
    {
      href: '#vehicle-sales',
      sectionId: '#vehicle-sales',
      text: 'Sale Listings',
      reportItemsCount: automobile?.listingsCount,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      notificationSection: 'sales',
    },
    {
      href: '#vehicle-damage-history',
      sectionId: '#vehicle-damage-history',
      text: 'Damage History',
      reportItemsCount: automobile?.damageHistoryCount,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      icon: <Lock fontSize="small" sx={{ marginRight: '-6px' }} />,
      showIcon:
        !automobile?.nmvtis_success && automobile?.damageHistoryCount === 0,
    },
    {
      href: '#vehicle-thefts',
      sectionId: '#vehicle-thefts',
      text: 'Theft Records',
      reportItemsCount: automobile?.theftsCount,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      notificationSection: 'thefts',
    },
    {
      href: '#vehicle-service-and-repairs',
      sectionId: '#vehicle-service-and-repairs',
      text: 'Maintenance',
      reportItemsCount: automobile?.servicesAndRepairsCount,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      notificationSection: 'maintenance',
    },
    {
      href: '#vehicle-ownership-history',
      sectionId: 'vehicle-ownership-history',
      text: 'Ownership History',
      reportItemsCount: automobile?.titleRecordsCount,
      hideReportItemsCount: false,
      visible: !isYMMSearch,
      icon: <Lock fontSize="small" sx={{ marginRight: '-6px' }} />,
      showIcon:
        !automobile?.nmvtis_success && automobile?.titleRecordsCount === 0,
    },
    {
      href: '#vehicle-recalls',
      sectionId: '#vehicle-recalls',
      text: 'Recalls',
      reportItemsCount: isYMMSearch
        ? automobile?.recallsCount
        : automobile?.recallsWithStatusCount,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'prices',
    },
    {
      href: '#vehicle-market',
      sectionId: '#vehicle-market',
      text: 'Market Value',
      reportItemsCount: automobile?.marketValueCount,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'market',
    },
    {
      href: '#vehicle-ownership-cost',
      sectionId: '#vehicle-ownership-cost',
      text: 'Ownership Cost',
      reportItemsCount: automobile?.costsCount,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'costs',
    },
    {
      href: '#vehicle-specs',
      sectionId: '#vehicle-specs',
      text: 'Vehicle Specs',
      reportItemsCount: automobile?.specsCount,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'specifications',
    },
    {
      href: '#vehicle-warranty',
      sectionId: '#vehicle-warranty',
      text: 'Warranties',
      reportItemsCount: automobile?.warrantiesCount,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'warranties',
    },
    {
      href: '#vehicle-safety-ratings',
      sectionId: '#vehicle-safety-ratings',
      text: 'Vehicle Safety',
      reportItemsCount: safetyCount,
      hideReportItemsCount: false,
      visible: true,
      notificationSection: 'safety',
    },
  ];

  const moveNavLink = (
    navLinkArray: VehicleNavLinkData[],
    sectionIdToMove: string,
    sectionIdAfter: string,
    GAEventLabel: string
  ) => {
    const currentIndex = navLinkArray.findIndex(
      (item) => item.sectionId === sectionIdToMove
    );

    const newIndex = navLinkArray.findIndex(
      (item) => item.sectionId === sectionIdAfter
    );

    if (currentIndex !== -1 && newIndex !== -1) {
      const [movedNavLink] = navLinkArray.splice(currentIndex, 1);
      (movedNavLink as NavLinkData).GAEventLabel = GAEventLabel;
      navLinkArray.splice(newIndex + 1, 0, movedNavLink);
    }
  };

  moveNavLink(
    navLinkData,
    'vehicle-ownership-history',
    '#vehicle-overview',
    'ownership history NEW'
  );
  moveNavLink(
    navLinkData,
    '#vehicle-specs',
    'vehicle-ownership-history',
    'vehicle specifications NEW'
  );

  if (shouldShowSmartInsightsNavLink) {
    moveNavLink(
      navLinkData,
      '#smart-insights',
      '#vehicle-overview',
      'smart insights NEW'
    );
  }

  return navLinkData.filter((item) => item.visible);
};
